package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppBadge
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.layout.AppHorizontalScrollbar
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.domain.model.CompanyRef
import br.com.vvrefeicoes.portal.domain.model.CustomerSatisfaction
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.feature.shell.LocalGlobalFilters
import com.composables.icons.lucide.BadgePercent
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.ThumbsUp
import com.composables.icons.lucide.Vote
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.operational_satisfaction_col_bad
import portalvv.shared.generated.resources.operational_satisfaction_col_business_days
import portalvv.shared.generated.resources.operational_satisfaction_col_date
import portalvv.shared.generated.resources.operational_satisfaction_col_excellent
import portalvv.shared.generated.resources.operational_satisfaction_col_goal
import portalvv.shared.generated.resources.operational_satisfaction_col_good
import portalvv.shared.generated.resources.operational_satisfaction_col_location
import portalvv.shared.generated.resources.operational_satisfaction_col_manager
import portalvv.shared.generated.resources.operational_satisfaction_col_meals
import portalvv.shared.generated.resources.operational_satisfaction_col_participation
import portalvv.shared.generated.resources.operational_satisfaction_col_regular
import portalvv.shared.generated.resources.operational_satisfaction_col_status
import portalvv.shared.generated.resources.operational_satisfaction_col_total_clients
import portalvv.shared.generated.resources.operational_satisfaction_col_total_votes
import portalvv.shared.generated.resources.operational_satisfaction_empty
import portalvv.shared.generated.resources.operational_satisfaction_indicator_positive
import portalvv.shared.generated.resources.operational_satisfaction_not_reached
import portalvv.shared.generated.resources.operational_satisfaction_reached
import kotlin.math.abs
import kotlin.math.roundToLong

private val TABLE_WIDTH = 1240.dp

@Composable
fun SatisfactionContent(
    unit: CompanyRef,
    month: MonthPeriod,
) {
    val controller = rememberCustomerSatisfactionController()
    val filters = LocalGlobalFilters.current

    LaunchedEffect(controller, unit, month, filters.units) {
        controller.applyUnit(unit.name, filters.units.map { it.name }.toSet())
        controller.applyPeriod(month)
        controller.init()
    }

    if (controller.loading) {
        Box(
            modifier = Modifier.fillMaxWidth().height(240.dp),
            contentAlignment = Alignment.Center,
        ) {
            CircularProgressIndicator()
        }
    } else {
        SatisfactionIndicators(controller.records)
        SatisfactionTable(controller.records)
    }
}

@Composable
private fun SatisfactionIndicators(records: List<CustomerSatisfaction>) {
    val totalVotes = records.sumOf { it.totalVotes }
    val totalClients = records.sumOf { it.totalClients }
    val positiveVotes = records.sumOf { it.excellent + it.good }
    val participation = if (totalClients == 0) 0.0 else totalVotes.toDouble() / totalClients * 100
    val positive = if (totalVotes == 0) 0.0 else positiveVotes.toDouble() / totalVotes * 100

    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space16),
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space24),
            verticalAlignment = Alignment.Top,
        ) {
            IndicatorItem(
                icon = Lucide.Vote,
                iconColor = AppPalette.Primary500,
                iconContainerColor = AppPalette.Primary100,
                label = stringResource(Res.string.operational_satisfaction_col_total_votes),
                value = totalVotes.toString(),
            )
            IndicatorItem(
                icon = Lucide.BadgePercent,
                iconColor = AppPalette.Info500,
                iconContainerColor = AppPalette.Info100,
                label = stringResource(Res.string.operational_satisfaction_col_participation),
                value = percent(participation),
            )
            IndicatorItem(
                icon = Lucide.ThumbsUp,
                iconColor = AppPalette.Success500,
                iconContainerColor = AppPalette.Success100,
                label = stringResource(Res.string.operational_satisfaction_indicator_positive),
                value = percent(positive),
            )
        }
    }
}

@Composable
private fun RowScope.IndicatorItem(
    icon: ImageVector,
    iconColor: Color,
    iconContainerColor: Color,
    label: String,
    value: String,
) {
    Row(
        modifier = Modifier.weight(1f),
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Surface(
            modifier = Modifier.size(36.dp),
            shape = AppShapes.small,
            color = iconContainerColor,
        ) {
            Box(contentAlignment = Alignment.Center) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = iconColor,
                    modifier = Modifier.size(16.dp),
                )
            }
        }
        Column(
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space4),
            horizontalAlignment = Alignment.Start,
        ) {
            Text(
                text = label,
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Text(
                text = value,
                style = MaterialTheme.typography.titleMedium,
                color = MaterialTheme.colorScheme.onSurface,
            )
        }
    }
}

@Composable
private fun SatisfactionTable(records: List<CustomerSatisfaction>) {
    val scrollState = rememberScrollState()

    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
            val tableWidth = maxOf(maxWidth, TABLE_WIDTH)
            Box(
                modifier = Modifier.fillMaxWidth().horizontalScroll(scrollState),
                contentAlignment = Alignment.TopStart,
            ) {
                Column(modifier = Modifier.width(tableWidth)) {
                    HeaderRow()
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    if (records.isEmpty()) {
                        EmptyState(stringResource(Res.string.operational_satisfaction_empty))
                    } else {
                        records.forEachIndexed { index, record ->
                            BodyRow(record)
                            if (index < records.lastIndex) {
                                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                            }
                        }
                    }
                }
            }
        }
    }

    AppHorizontalScrollbar(
        state = scrollState,
        modifier = Modifier.fillMaxWidth().padding(bottom = AppSpacing.space4),
    )
}

@Composable
private fun HeaderRow() {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space12),
        horizontalArrangement = Arrangement.Start,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        HeaderCell(stringResource(Res.string.operational_satisfaction_col_date), 1.0f, TextAlign.Start)
        HeaderCell(stringResource(Res.string.operational_satisfaction_col_manager), 1.6f, TextAlign.Start)
        HeaderCell(stringResource(Res.string.operational_satisfaction_col_location), 1.0f, TextAlign.Start)
        HeaderCell(stringResource(Res.string.operational_satisfaction_col_meals), 0.9f, TextAlign.End)
        HeaderCell(stringResource(Res.string.operational_satisfaction_col_business_days), 0.9f, TextAlign.End)
        HeaderCell(stringResource(Res.string.operational_satisfaction_col_total_clients), 1.1f, TextAlign.End)
        HeaderCell(stringResource(Res.string.operational_satisfaction_col_excellent), 0.8f, TextAlign.End)
        HeaderCell(stringResource(Res.string.operational_satisfaction_col_good), 0.8f, TextAlign.End)
        HeaderCell(stringResource(Res.string.operational_satisfaction_col_regular), 0.8f, TextAlign.End)
        HeaderCell(stringResource(Res.string.operational_satisfaction_col_bad), 0.8f, TextAlign.End)
        HeaderCell(stringResource(Res.string.operational_satisfaction_col_total_votes), 1.0f, TextAlign.End)
        HeaderCell(stringResource(Res.string.operational_satisfaction_col_goal), 0.8f, TextAlign.End)
        HeaderCell(stringResource(Res.string.operational_satisfaction_col_participation), 1.0f, TextAlign.End)
        HeaderCell(stringResource(Res.string.operational_satisfaction_col_status), 1.0f, TextAlign.Start)
    }
}

@Composable
private fun BodyRow(record: CustomerSatisfaction) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space12),
        horizontalArrangement = Arrangement.Start,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        BodyCell(dateLabel(record.date), 1.0f, TextAlign.Start)
        BodyCell(record.managerName, 1.6f, TextAlign.Start)
        BodyCell(record.location, 1.0f, TextAlign.Start)
        BodyCell(record.meals.toString(), 0.9f, TextAlign.End)
        BodyCell(record.businessDays.toString(), 0.9f, TextAlign.End)
        BodyCell(record.totalClients.toString(), 1.1f, TextAlign.End)
        BodyCell(record.excellent.toString(), 0.8f, TextAlign.End)
        BodyCell(record.good.toString(), 0.8f, TextAlign.End)
        BodyCell(record.regular.toString(), 0.8f, TextAlign.End)
        BodyCell(record.bad.toString(), 0.8f, TextAlign.End)
        BodyCell(record.totalVotes.toString(), 1.0f, TextAlign.End)
        BodyCell(percent(record.goalPercent), 0.8f, TextAlign.End)
        BodyCell(percent(record.participationPercent), 1.0f, TextAlign.End)
        Box(
            modifier = Modifier.weight(1.0f).padding(horizontal = AppSpacing.space8),
            contentAlignment = Alignment.TopStart,
        ) {
            StatusBadge(record.achieved)
        }
    }
}

@Composable
private fun RowScope.HeaderCell(
    text: String,
    weight: Float,
    align: TextAlign,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        textAlign = align,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        modifier = Modifier.weight(weight).padding(horizontal = AppSpacing.space8),
    )
}

@Composable
private fun RowScope.BodyCell(
    text: String,
    weight: Float,
    align: TextAlign,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.onSurface,
        textAlign = align,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        modifier = Modifier.weight(weight).padding(horizontal = AppSpacing.space8),
    )
}

@Composable
private fun StatusBadge(achieved: Boolean) {
    AppBadge(
        text =
            stringResource(
                if (achieved) {
                    Res.string.operational_satisfaction_reached
                } else {
                    Res.string.operational_satisfaction_not_reached
                },
            ),
        containerColor = if (achieved) AppPalette.Success100 else AppPalette.Error100,
        contentColor = if (achieved) AppPalette.Success900 else AppPalette.Error900,
    )
}

private fun dateLabel(iso: String): String =
    if (iso.length >= 10) {
        "${iso.substring(8, 10)}/${iso.substring(5, 7)}/${iso.substring(0, 4)}"
    } else {
        iso
    }

private fun percent(value: Double): String {
    val scaled = (value * 100).roundToLong()
    val reais = scaled / 100
    val cents = abs(scaled % 100)
    return "$reais,${cents.toString().padStart(2, '0')}%"
}
